import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { ArrowRight, FilePenLine } from 'lucide-react';

interface AgentSelectionDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectManual: () => void;
}

export function AgentSelectionDrawer({ 
  open, 
  onOpenChange, 
  onSelectManual,
}: AgentSelectionDrawerProps) {
  const options = [
    {
      title: 'Start from scratch',
      description: 'Set up the agent yourself, then add instructions and knowledge.',
      action: 'Create manually',
      onClick: onSelectManual,
      icon: FilePenLine,
    },
  ];

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <div className="mx-auto w-full max-w-3xl px-5 pb-8 sm:px-8 sm:pb-10">
          <DrawerHeader className="px-0 pb-5 pt-7 text-left">
            <DrawerTitle className="text-xl font-semibold tracking-tight">
              Create an agent
            </DrawerTitle>
            <DrawerDescription className="mt-1 max-w-lg leading-relaxed">
              Choose a starting point. You can edit everything later.
            </DrawerDescription>
          </DrawerHeader>

          <div className="grid gap-3 overflow-y-auto">
            {options.map((option) => {
              const Icon = option.icon;

              return (
                <button
                  key={option.title}
                  type="button"
                  onClick={option.onClick}
                  className="group flex min-h-44 flex-col rounded-xl border border-border bg-card p-5 text-left transition-colors hover:border-foreground/20 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground transition-colors group-hover:text-foreground">
                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                  </div>

                  <div className="mt-5">
                    <h3 className="text-sm font-semibold text-foreground">
                      {option.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
                      {option.description}
                    </p>
                  </div>

                  <div className="mt-auto flex items-center gap-1.5 pt-5 text-sm font-medium text-foreground">
                    <span>{option.action}</span>
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
